import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, Plus, Trash2, CheckCircle2, Rocket, GraduationCap, Database, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { OpenNewYearWizard } from '../../components/school/OpenNewYearWizard';
import { PromoteStudentsWizard } from '../../components/school/PromoteStudentsWizard';
import { toast } from 'sonner';

export default function AcademicYearsPage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = useOrgId();
  const { fetchYears: refreshYearContext } = useAcademicYear();
  const [years, setYears] = useState([]);
  const [termsByYear, setTermsByYear] = useState({});
  const [loading, setLoading] = useState(true);
  const [yearDialog, setYearDialog] = useState(false);
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_active: true });
  const [termDialog, setTermDialog] = useState(null);
  const [termForm, setTermForm] = useState({ name: '', sequence: 1 });
  const [saving, setSaving] = useState(false);
  const [openWizard, setOpenWizard] = useState(false);
  const [promoteWizard, setPromoteWizard] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      const ys = await schoolApi.listYears(orgId);
      setYears(ys);
      const map = {};
      await Promise.all(ys.map(async (y) => { map[y.id] = await schoolApi.listTerms(orgId, y.id); }));
      setTermsByYear(map);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const afterYearChange = async () => { await load(); await refreshYearContext(); };

  const setActive = async (id) => {
    try { await schoolApi.updateYear(id, { is_active: true }); toast.success('Active year updated'); afterYearChange(); }
    catch (e) { toast.error('Failed to set active'); }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await schoolApi.backfillYear({ organization_id: orgId });
      const u = res.updated || {};
      toast.success(`Backfill done: ${u.classes || 0} classes, ${u.sections || 0} sections, ${u.subjects || 0} subjects tagged`);
      afterYearChange();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Backfill failed'); }
    finally { setBackfilling(false); }
  };

  const saveYear = async () => {
    if (!yearForm.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      await schoolApi.createYear({ organization_id: orgId, ...yearForm });
      toast.success(t('common.saved')); setYearDialog(false); setYearForm({ name: '', start_date: '', end_date: '', is_active: true }); afterYearChange();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const saveTerm = async () => {
    if (!termForm.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      await schoolApi.createTerm({ organization_id: orgId, academic_year_id: termDialog.id, name: termForm.name, sequence: Number(termForm.sequence) || 1 });
      toast.success(t('common.saved')); setTermDialog(null); setTermForm({ name: '', sequence: 1 }); load();
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const delYear = async (id) => { try { await schoolApi.deleteYear(id); toast.success(t('common.deleted')); afterYearChange(); } catch (e) { toast.error('Failed'); } };
  const delTerm = async (id) => { try { await schoolApi.deleteTerm(id); toast.success(t('common.deleted')); load(); } catch (e) { toast.error('Failed'); } };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('years.title')} subtitle={t('years.subtitle')} icon={Calendar}
        actions={
          <div className="flex flex-wrap gap-2">
            {user?.role !== 'teacher' && (
              <Button variant="outline" onClick={runBackfill} disabled={backfilling} className="gap-2" data-testid="backfill-button" title="Tag existing classes/sections/subjects with the active year">
                {backfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />} <span className="hidden sm:inline">Backfill</span>
              </Button>
            )}
            <Button variant="outline" onClick={() => setPromoteWizard(true)} className="gap-2" data-testid="promote-students-button"><GraduationCap className="w-4 h-4" /> <span className="hidden sm:inline">Promote Students</span></Button>
            <Button onClick={() => setOpenWizard(true)} className="gap-2" data-testid="open-new-year-button"><Rocket className="w-4 h-4" /> {t('years.new', 'Open New Year')}</Button>
          </div>
        } />

      {loading ? <LoadingState rows={4} /> : years.length === 0 ? (
        <Card><EmptyState icon={Calendar} title={t('common.noData')} description={t('years.subtitle')} actionLabel={t('years.new')} onAction={() => setYearDialog(true)} /></Card>
      ) : (
        <div className="space-y-4">
          {years.map((y) => (
            <Card key={y.id} className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center"><Calendar className="w-5 h-5 text-primary" /></div>
                  <div>
                    <p className="font-semibold flex items-center gap-2">{y.name} {y.is_active && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" /> {t('common.active')}</Badge>}</p>
                    <p className="text-xs text-muted-foreground">{y.start_date || '?'} &rarr; {y.end_date || '?'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {!y.is_active && <Button size="sm" variant="outline" onClick={() => setActive(y.id)} data-testid={`set-active-${y.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {t('common.active', 'Set Active')}</Button>}
                  <Button size="sm" variant="outline" onClick={() => { setTermDialog(y); setTermForm({ name: '', sequence: (termsByYear[y.id]?.length || 0) + 1 }); }} data-testid={`add-term-${y.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> {t('years.addTerm')}</Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => delYear(y.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(termsByYear[y.id] || []).map((term) => (
                  <span key={term.id} className="inline-flex items-center gap-1 bg-muted/60 rounded px-2.5 py-1 text-xs">
                    {term.name}
                    <button onClick={() => delTerm(term.id)} className="text-muted-foreground hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </span>
                ))}
                {(termsByYear[y.id] || []).length === 0 && <span className="text-xs text-muted-foreground">No terms yet</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={yearDialog} onOpenChange={setYearDialog}>
        <DialogContent data-testid="year-dialog">
          <DialogHeader><DialogTitle>{t('years.new')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('common.name')} *</Label><Input value={yearForm.name} onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })} placeholder="2025-2026" data-testid="year-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('years.startDate')}</Label><Input type="date" value={yearForm.start_date} onChange={(e) => setYearForm({ ...yearForm, start_date: e.target.value })} /></div>
              <div><Label>{t('years.endDate')}</Label><Input type="date" value={yearForm.end_date} onChange={(e) => setYearForm({ ...yearForm, end_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setYearDialog(false)}>{t('common.cancel')}</Button><Button onClick={saveYear} disabled={saving} data-testid="year-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!termDialog} onOpenChange={(o) => !o && setTermDialog(null)}>
        <DialogContent data-testid="term-dialog">
          <DialogHeader><DialogTitle>{t('years.addTerm')} &mdash; {termDialog?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t('common.name')} *</Label><Input value={termForm.name} onChange={(e) => setTermForm({ ...termForm, name: e.target.value })} placeholder="Term 1" data-testid="term-name-input" /></div>
            <div><Label>#</Label><Input type="number" value={termForm.sequence} onChange={(e) => setTermForm({ ...termForm, sequence: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTermDialog(null)}>{t('common.cancel')}</Button><Button onClick={saveTerm} disabled={saving} data-testid="term-save-button">{saving ? t('common.saving') : t('common.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <OpenNewYearWizard open={openWizard} onOpenChange={setOpenWizard} orgId={orgId} years={years} onDone={afterYearChange} />
      <PromoteStudentsWizard open={promoteWizard} onOpenChange={setPromoteWizard} orgId={orgId} years={years} onDone={afterYearChange} />
    </div>
  );
}
